import React, { ComponentType, KeyboardEvent, MouseEvent } from "react";
import { render, unmountComponentAtNode } from "react-dom";

import { CheckBoxFake, FAKE_COMPONENT_VALUE_LOC_KEY, FakeContainerProps } from "@multiweb/reactComponents";
import { loadLocalizationIfNeeded } from "@multiweb/reactCore/i18n";
import { createRootComponent, RootComponentClass, StoreManager } from "@multiweb/reactSetup";
import { isCheckBoxSwitchKey, isMultiEditEraseKey } from "@multiweb/reactUtils";
import { IDisposable } from "Global/Base/IDisposable";
import { AnyConstructor } from "Global/Base/types";
import { FakeComponentValueState } from "UI/UI/UI/Controls/ControlEnums";
import { AppContext } from "../../../App";
import { ITextBuilder } from "../../../Global/Base/Builders/ITextBuilder";
import { IEditableControl, IGenericControl, ILocalizable, IMultiEdit } from "../../UI/UI/Controls/ControlInterfaces";

/**
 * Přidá podporu pro hromadnou editaci k zadané komponentě. Podporuje původní komponenty i konektory pro react komponenty.
 *
 * Poznámka:
 * React komponenty by ideálně měly mít podporu pro hromadnou editaci vestavěnou, takže po přepsání všech komponent již nebude
 * tento mixin potřebný. V react komponentách lze využít pomocnou komponentu {@link MultiEditWrapper}.
 *
 * @param Base Základní komponenta, která se rozšíří o podporu hromadné editace.
 * @param FakeComponent Fake komponenta zobrazovaná po aktivaci hromadné editace.
 */
export const MultiEditMixin = <T extends AnyConstructor<IEditableControl & IGenericControl & ILocalizable & IDisposable>>
    (Base: T, FakeComponent: ComponentType<FakeContainerProps>) => class MultiEditMixin extends Base implements IMultiEdit, IDisposable {
        /**
         * Css třída pro skrytí elementu reálné komponenty při aktivní hromadné editaci.
         * Pro skrytí nestačí nastavení hidden atributu, protože ten může být potlačen
         * nastavením display pravidla ve stylech komponenty.
         */
        public static readonly HIDDEN_CLASS = "multi-edit-mixin-hidden";

        /**
         * Příznak, zda je aktivní hromadná editace.
         */
        public _multiEditActive = false;

        /**
         * Příznak, zda proběhla interakce s fake komponentou v rámci jednoho cyklu hromadné editace.
         * (vypnutí hromadné editace příznak resetuje)
         */
        public __touched = false;
        /**
         * Řeší problém při změně výběru. Při setování controlValue je potřeba rozlišit, jestli hodnota null přišla v reakci na stisk backspace nebo to je načtená hodnota z db a podle toho vyrenderovat fake komponentu.
         */
        public _valueState: FakeComponentValueState;

        /**
         * Element, do kterého se vykresluje fake komponenta.
         */
        public _fakeRootElement: HTMLElement;

        /**
         * Příznak, zda je fake komponenta checkbox. Checkbox má odlišné chování v tom, že nepodporuje explicitní smazání hodnoty pomocí klávesy backspace/delete. Naopak podporuje změnu hodnoty
         * pomocí klávesy space. Kliknutí na checkbox rovnou mění hodnotu, kdežto u ostatních komponent způsobí pouze zaměření.
         */
        public readonly _isCheckbox: boolean = FakeComponent === CheckBoxFake;

        /** Kořenová komponenta, do které se vkládá předaná react komponenta (parametr FakeComponent). Poskytuje pro své potomky přístup k redux stavu a zachytává chyby z komponent, reducerů a ság. */
        public readonly _rootComponent: RootComponentClass;

        public get touched(): boolean {
            return this._touched;
        }
        /**
         * Id elementu s popiskem nebo undefined, pokud pro komponentu popisek neexistuje. Využívá se pro přístupnost fake komponent.
         */
        public readonly _labelId: string | undefined;

        constructor(...args: any[]) {
            super(...args);

            const isDev = !!AppContext.instance.configuration.developmentMode;
            StoreManager.initializeStore(isDev);
            this._rootComponent = createRootComponent(isDev);

            // sestavení labelId spoléhá na stejný postup v ControlElement.ts getCaptionElement a nastavení componentId atributu na komponentu v _createCaption.
            const componentId = this.element.getAttribute("data-componentId");
            this._labelId = componentId ? `${componentId}-label` : undefined;
        }

        public get multiEditActive(): boolean {
            return this._multiEditActive;
        }

        public set multiEditActive(value) {
            // počítá se s tím, že tato property bude nasetována při změně výběru, toho se využije k tomu,
            // že resetuje svůj stav
            this. _valueState = FakeComponentValueState.notSet;
            this._multiEditActive = value;
            if (this._multiEditActive && !this.touched) {
                loadLocalizationIfNeeded(FAKE_COMPONENT_VALUE_LOC_KEY);
                this._replaceWithFake();
            }
            if (!this._multiEditActive) { //reset příznaku při vypnutí hromadné editace
                this._returnToOriginal();
            }
        }

        public override set locKey(value: string) {
            super.locKey = value;
            loadLocalizationIfNeeded(value);
            this._renderFakeComponentIfPresent();
        }

        public override get locKey(): string {
            return super.locKey;
        }

        public override set isVisuallyEncrypted(value: boolean) {
            super.isVisuallyEncrypted = value;
            this._renderFakeComponentIfPresent();
        }

        public override get isVisuallyEncrypted(): boolean {
            return super.isVisuallyEncrypted;
        }

        public override set enabledVisual(value: boolean) {
            super.enabledVisual = value;
            if (!this.isEditing || !this.touched) {
                this._renderFakeComponentIfPresent();
            }
        }

        public override get enabledVisual(): boolean {
            return super.enabledVisual;
        }

        public override set displayedValue(value: ITextBuilder) {
            super.displayedValue = value;
        }

        public override get displayedValue(): ITextBuilder | null {
            if (this._multiEditActive) {
                return null;
            } else {
                return super.displayedValue;
            }
        }

        public override set isEditing(value: boolean) {
            super.isEditing = value;
            if (!this.isEditing) {
                this._touched = false;
            } else if (!this.isEditing || !this.touched) {
                this._renderFakeComponentIfPresent();
            }
        }

        public override get isEditing(): boolean {
            return super.isEditing;
        }

        public override set controlValue(value: any) {
            super.controlValue = value;
            if (value)
                this._valueState = FakeComponentValueState.userChanged;
            //komponentu je potřeba nastavit jako změněnou pokud uživatel smazal hodnotu přes fake nebo nastavil konkrétní hodnotu
            if (this._valueState !== FakeComponentValueState.notSet && !this._touched)
                this._touched = true;
        }

        public override get controlValue(): any {
            return super.controlValue;
        }

        /**
         * Nastaví příznak, zda proběhla interakce s fake komponentou.
         * @param value Hodnota true se nastaví pouze při aktivní hromadné editaci.
         */
        public set _touched(value: boolean) {
            this.__touched = this.multiEditActive && value;
            if (this._touched) {
                this._returnToOriginal();
            }
        }

        /**
         * Popis viz {@link __touched}.
         */
        public get _touched(): boolean {
            return this.__touched;
        }

        /**
         * Nahradí originální komponentu za fake komponentu, pokud ještě nahrazená není.
         */
        public _replaceWithFake(): void {
            if (this._fakeRootElement) {
                return; // fake komponenta je již zapojená
            }

            this._fakeRootElement = document.createElement(this.element.tagName);
            this._fakeRootElement.classList.add("fake-root-element");
            // Některé layouty, např. v tabulce, nastavují pevnou šířku na element. Fake komponenta tuto šířku musí převzít.
            this._fakeRootElement.style.width = this.element.style.width;
            this.element.insertAdjacentElement("afterend", this._fakeRootElement);
            this.element.hidden = true;
            this.element.classList.add(MultiEditMixin.HIDDEN_CLASS);
            this._renderFakeComponentIfPresent();
        }

        /**
         * Vypočítá hodnotu disabled prop na základě jiných příznaků.
         */
        public _computeDisabled(): boolean {
            return !this.enabledVisual;
        }

        /**
         * Provede vykreslení fake komponenty, pokud bylo zobrazování přepnuto na fake komponentu.
         * V opačném případě nic nedělá.
         */
        public _renderFakeComponentIfPresent(): void {
            if (!this._fakeRootElement) {
                return;
            }

            render(
                <this._rootComponent>
                    <FakeComponent
                        onClick={this._handleOnClick}
                        onKeyDown={this._handleOnKeyDown}
                        locKey={this.locKey}
                        disabled={this._computeDisabled()}
                        isEditing={this.isEditing}
                        ariaLabelledBy={this._labelId}
                        required={this.required}
                        recommended={this.recommended}
                        postponedRequired={this.postponedRequired}
                    />
                </this._rootComponent>, this._fakeRootElement);
        }

        /**
         * Odstraní fake komponentu a vrátí originál, pokud je fake komponenta zobrazená.
         */
        public _returnToOriginal(): void {
            if (!this._fakeRootElement) {
                return;
            }
            unmountComponentAtNode(this._fakeRootElement);
            this._fakeRootElement.remove();
            this._fakeRootElement = undefined;
            this.element.hidden = false;
            this.element.classList.remove(MultiEditMixin.HIDDEN_CLASS);
        }

        /**
         * Zpracuje událost kliknutí primárního tlačítka myši.
         *
         * Aktivuje režim editace, pokud ještě aktivní není.
         *
         * Pokud je fake komponenta checkbox, přepne na originální komponentu a přepne hodnotu.
         * @param event Událost myši.
         */
        public _handleOnClick = (event: MouseEvent) => {
            this.clicked.trigger(this, event.nativeEvent); // aktivuje režim editace, pokud ještě aktivní není

            if (this._isCheckbox) {
                this._touched = true;
                this.startEdit();
            }
        };

        /**
         * Zpracuje událost stisku klávesy. Pokud je komponenta disabled, nic nedělá.
         *
         * Pokud se nejedná o checkbox a byla stisknuta klávesa pro smazání hodnoty, tak smaže hodnotu atributu, přepne na originální komponentu a zahájí editaci.
         *
         * Pokud se jedná o checkbox a byla stisknuta klávesa pro přepnutí hodnoty checkboxu, přepne na originální komponentu a zahájí editaci.
         *
         * @param event Událost stisku klávesy.
         */
        public _handleOnKeyDown = (event: KeyboardEvent) => {
            if (this._computeDisabled()) {
                return;
            }

            if (this._isCheckbox && isCheckBoxSwitchKey(event)) {
                this._touched = true;
                this.startEdit();
            } else if (!this._isCheckbox && isMultiEditEraseKey(event)) {
                this._touched = true;
                this._valueState = FakeComponentValueState.userCleared;
                this.valueChanged.trigger(this, null);
                this.startEdit();
            }
        };

        public override dispose(): void {
            if (this._fakeRootElement) {
                unmountComponentAtNode(this._fakeRootElement);
            }
            super.dispose();
        }
    };
